// AppContainer.tsx
import React, { useEffect, Suspense, lazy } from "react";
import { HashRouter as Router, Route, Switch } from "react-router-dom";
import { onMessage } from "firebase/messaging";
import { notification } from "antd";
import { messaging } from "./firebase/firebase-config";
import AOS from "aos";
import "aos/dist/aos.css";

// Lazy-loaded pages
const Login = lazy(() => import("./authentication/Login"));
const Dashboard = lazy(() => import("./main/dashboard/Index"));
const Temple = lazy(() => import("./main/temple/Temple"));
const TempleRegister = lazy(() => import("./main/temple/templeRegister"));
const Project = lazy(() => import("./main/project/Project"));
const ProjectForm = lazy(() => import("./main/project/TempleProjectRegister"));
const Volunteer = lazy(() => import("./main/volunteer/volunteer"));
const Profile = lazy(() => import("./main/profile/Profile"));
const AllUserDetails = lazy(() => import("./main/user/UserDetails"));
const NotificationPage = lazy(() => import("./main/notification/notification"));
const NotificationRegister = lazy(() =>
  import("./main/notification/NotificationRegister"),
);
const LoadingFallback = lazy(() => import("./pages/Loading"));
const SloganForDay = lazy(() => import("./main/DaySlogan/slogan"));
const Home = lazy(() => import("./main/home/home"));
const Contact = lazy(() => import("./main/home/contact"));
const About = lazy(() => import("./main/home/about"));
const EventApprove = lazy(() => import("./main/Event/Event"));
const VlounteerRemove = lazy(() => import("./main/volunteer/vlonteerRemove"));
const VolunteerDelete = lazy(() => import("./main/volunteer/vlonteerDelete"));
const Page404 = lazy(() => import("./pages/errorpages/404"));
const TempleUser = lazy(() => import("./main/home/temple"));
const SponserDetails = lazy(() => import("./main/sponser/SponserProduction"));
const SponserApprove = lazy(() => import("./main/sponser/SponserApprove"));
const MappingSponser = lazy(() => import("./main/sponser/MappingSponser"));
const InchargeProject = lazy(() => import("./main/sponser/InchargeProject"));
const AddTempleUsersWithRecaptcha = lazy(() => import("./main/home/Addtemple"));
const AllUserRequiredTempleDetails = lazy(() => import("./main/Required"));
const SponserApproveDetails = lazy(() => import("./main/sponser/SponserList"));
const SponsorInstallation = lazy(() =>
  import("./main/sponser/SponsorInstallation"),
);
const SponserProduction = lazy(() =>
  import("./main/sponser/SponserProduction"),
);
const UserTempleProjectRegister = lazy(() =>
  import("./main/home/UserTempleAdd"),
);
const AdminTempleProjectRegister = lazy(() =>
  import("./main/temple/AdminTempleAdd"),
);
const InstallGuide = lazy(() => import("./main/home/GuidePage"));
const VideoPostList = lazy(() => import("./main/VideoUpload/index"));
const ComplainsTable = lazy(() => import("./main/complains/complains"));
const AdminNotification = lazy(() =>
  import("./main/profile/AdminNotification"),
);
const FeedBackForUser = lazy(() => import("./main/feedback"));
const ErrorBoundary = lazy(() => import("./pages/ErrorBoundary"));

// Protected route wrapper
import ProtectedRoute from "./authentication/ProtectedRoute";

// Route configuration
const routes = [
  { path: "/", component: Home, exact: true, protected: false },
  { path: "/login", component: Login, exact: true, protected: false },
  { path: "/about", component: About, exact: true, protected: false },
  { path: "/contact", component: Contact, exact: true, protected: false },
  { path: "/templesearch", component: TempleUser, protected: false },
  {
    path: "/add-temple-user",
    component: UserTempleProjectRegister,
    protected: false,
  },
  { path: "/install-guide", component: InstallGuide, protected: false },
  {
    path: "/add-temple-admin",
    component: AdminTempleProjectRegister,
    protected: true,
  },
  {
    path: "/addtemple",
    component: AddTempleUsersWithRecaptcha,
    exact: true,
    protected: true,
  },

  // Protected routes
  { path: "/dashboard", component: Dashboard, protected: true },
  { path: "/temple", component: Temple, protected: true },
  { path: "/templeregister", component: TempleRegister, protected: true },
  { path: "/project", component: Project, protected: true },
  { path: "/projectregister", component: ProjectForm, protected: true },
  { path: "/volunteerApprove", component: Volunteer, protected: true },
  { path: "/vlounteerdetail", component: VlounteerRemove, protected: true },
  { path: "/vlounteerdelete", component: VolunteerDelete, protected: true },
  { path: "/profile", component: Profile, protected: true },
  { path: "/adminNotification", component: AdminNotification, protected: true },
  { path: "/feedback", component: FeedBackForUser, protected: true },
  { path: "/user", component: AllUserDetails, protected: true },
  { path: "/notification", component: NotificationPage, protected: true },
  {
    path: "/notificationregister",
    component: NotificationRegister,
    protected: true,
  },
  { path: "/slogan", component: SloganForDay, protected: true },
  { path: "/event", component: EventApprove, protected: true },
  {
    path: "/RequiredTemple",
    component: AllUserRequiredTempleDetails,
    protected: true,
  },
  { path: "/sponserdetail", component: SponserDetails, protected: true },
  {
    path: "/sponserapprovedetails",
    component: SponserApproveDetails,
    protected: true,
  },
  { path: "/sponsorproduction", component: SponserProduction, protected: true },
  {
    path: "/sponsorinstallation",
    component: SponsorInstallation,
    protected: true,
  },
  { path: "/videopost", component: VideoPostList, protected: true },
  { path: "/sponserapprove", component: SponserApprove, protected: true },
  { path: "/complains", component: ComplainsTable, protected: true },
  { path: "/mappingsponser", component: MappingSponser, protected: true },
  { path: "/incharge", component: InchargeProject, protected: true },

  { path: "*", component: Page404, protected: false },
];

const AppContainer = () => {
  useEffect(() => {
    console.log = function() {};

    // Initialize AOS
    AOS.init({ duration: 800, once: true });

    // Firebase foreground messages
    const unsubscribe = onMessage(messaging, (payload) => {
      try {
        notification.open({
          message: payload.notification?.title ?? "Notification",
          description: payload.notification?.body ?? "",
          onClick: () =>
            payload.data?.click_action &&
            window.open(payload.data.click_action),
          pauseOnHover: true,
          showProgress: true,
          style: { color: "green" },
        });
      } catch (err) {
        console.error("Notification error:", err);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <Router basename="/">
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Switch>
            {routes.map((route, idx) =>
              route.protected ? (
                <ProtectedRoute
                  key={idx}
                  path={route.path}
                  exact={route.exact}
                  component={route.component}
                />
              ) : (
                <Route
                  key={idx}
                  path={route.path}
                  exact={route.exact}
                  component={route.component}
                />
              ),
            )}
          </Switch>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
};

export default AppContainer;
